import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { getIntegrationMessageApiConfig } from './integration-message.utils';
import { useIntegrationMessagesFilter } from './hooks/useIntegrationMessagesFilter';
import type {
    IntegrationMessageDetailDto,
    IntegrationMessageKind,
    IntegrationMessageListItemDto,
    PurgeProcessedResponseDto,
    UpdateInboxMessageDto,
    UpdateInternalOutboxMessageDto,
    UpdateEmailOutboxMessageDto,
    UpdateOutboxMessageDto,
} from './integration-message.types';

export const useIntegrationMessagesListApi = (
    kind: IntegrationMessageKind,
    options?: { suspendFetching?: boolean },
) => {
    const axiosInstance = useAxiosAuth();
    const config = getIntegrationMessageApiConfig(kind);
    const { filter } = useIntegrationMessagesFilter(kind);
    const {
        take,
        skip,
        searchText,
        tenantId,
        type,
        id,
        aggregateId,
        externalId,
        isProcessed,
    } = filter;

    const { data, isLoading, error } = useQuery<{
        items: IntegrationMessageListItemDto[];
        totalCount: number;
    }>({
        queryKey: [
            config.queryKeys.list,
            kind,
            take,
            skip,
            searchText,
            tenantId,
            type,
            id,
            aggregateId,
            externalId,
            isProcessed,
        ],
        queryFn: async () => {
            const params: Record<string, string | number | boolean | undefined> = {
                skip,
                take,
                searchText: searchText || undefined,
            };

            if (tenantId) params.tenantId = tenantId;
            if (type) params.type = type;
            if (id) params.id = id;
            if (aggregateId) params.aggregateId = aggregateId;
            if (externalId) params.externalId = externalId;
            if (typeof isProcessed === 'boolean') params.isProcessed = isProcessed;

            const url = UrlHelper.buildUrlWithQueryParams(config.endpoints.list, params);
            const response = await axiosInstance.get<{
                items: IntegrationMessageListItemDto[];
                totalCount: number;
            }>(url);
            return response.data;
        },
        enabled: !options?.suspendFetching,
        placeholderData: keepPreviousData,
    });

    return { list: data, isLoading, error };
};

export const useIntegrationMessageDetailApi = (kind: IntegrationMessageKind, id?: string) => {
    const axiosInstance = useAxiosAuth();
    const config = getIntegrationMessageApiConfig(kind);

    const { data, isLoading, error } = useQuery<IntegrationMessageDetailDto>({
        queryKey: [config.queryKeys.detail, kind, id],
        enabled: Boolean(id),
        queryFn: async () => {
            const response = await axiosInstance.get<IntegrationMessageDetailDto>(
                `${config.endpoints.get}/${id}`,
            );
            return response.data;
        },
    });

    return { message: data, isLoading, error };
};

export const useIntegrationMessageMutations = (kind: IntegrationMessageKind) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();
    const config = getIntegrationMessageApiConfig(kind);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [config.queryKeys.list] });
    };

    const updateMutation = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: string;
            body: UpdateInboxMessageDto | UpdateOutboxMessageDto | UpdateInternalOutboxMessageDto | UpdateEmailOutboxMessageDto;
        }) => {
            await axiosInstance.put(`${config.endpoints.update}/${id}`, body);
        },
        onSuccess: (_, variables) => {
            invalidate();
            queryClient.invalidateQueries({
                queryKey: [config.queryKeys.detail, kind, variables.id],
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await axiosInstance.delete(`${config.endpoints.delete}/${id}`);
        },
        onSuccess: invalidate,
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            await axiosInstance.post(config.endpoints.bulkDelete, { ids });
        },
        onSuccess: invalidate,
    });

    const purgeProcessedMutation = useMutation({
        mutationFn: async (olderThanDays: number) => {
            const response = await axiosInstance.post<PurgeProcessedResponseDto>(
                config.endpoints.purgeProcessed,
                { olderThanDays },
            );
            return response.data;
        },
        onSuccess: invalidate,
    });

    const purgeAllMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosInstance.post<PurgeProcessedResponseDto>(
                config.endpoints.purgeAll,
            );
            return response.data;
        },
        onSuccess: invalidate,
    });

    return { updateMutation, deleteMutation, bulkDeleteMutation, purgeProcessedMutation, purgeAllMutation };
};
